import { create } from "zustand";
import { persist, PersistStorage } from "zustand/middleware";
import { Employee } from "domain/entities/employee";
import { Permission } from "domain/entities/permission";
import { getSessionStorageItem, setSessionStorageItem, removeSessionStorageItem } from "../utils/sessionStorage";

export type ImpersonationState = {
  isDebugMode: boolean;
  impersonatedEmployeeId: string | null;
  originalEmployee: Employee | undefined;
  originalPermissions: Permission[] | undefined;
  enableImpersonation: (
    impersonatedEmployeeId: string,
    originalEmployee: Employee,
    originalPermissions: Permission[]
  ) => void;
  disableImpersonation: () => void;
};

const createSessionStorageConfig = (): PersistStorage<ImpersonationState> => ({
  getItem: (key) => {
    try {
      const storedValue = getSessionStorageItem(key);
      return storedValue ? JSON.parse(storedValue) : null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      setSessionStorageItem(key, JSON.stringify(value));
    } catch {
      // Silently fail during build
    }
  },
  removeItem: (key) => {
    try {
      removeSessionStorageItem(key);
    } catch {
      // Silently fail during build
    }
  },
});

const isBuildTime = typeof window === 'undefined';

const createImpersonationStore = () => {
  const storeConfig = (set: any) => ({
    isDebugMode: false,
    impersonatedEmployeeId: null,
    originalEmployee: undefined,
    originalPermissions: undefined,
    enableImpersonation: (impersonatedEmployeeId: string, originalEmployee: Employee, originalPermissions: Permission[]) => {
      set({
        isDebugMode: true,
        impersonatedEmployeeId,
        originalEmployee,
        originalPermissions,
      });
    },
    disableImpersonation: () => {
      set({
        isDebugMode: false,
        impersonatedEmployeeId: null,
        originalEmployee: undefined,
        originalPermissions: undefined,
      });
    },
  });

  if (isBuildTime) {
    return create<ImpersonationState>(storeConfig);
  }

  return create(
    persist<ImpersonationState>(storeConfig, {
      name: "impersonation-session-storage",
      storage: createSessionStorageConfig(),
    })
  );
};

export const useImpersonationStore = createImpersonationStore();
